<template>
  <BaseButtonDropdown
    v-model="pickerOpen"
    class="c-base-time-picker"
    :class="[
      {
        'c-base-time-picker--dropdown tw:inline-flex tw:w-fit tw:max-w-full':
          variant === 'dropdown',
        'c-base-time-picker--open': pickerOpen,
      },
    ]"
    :disabled="disabled"
    new-design
    new-dropdown
    placement="bottom-start"
    @keydown.esc.prevent.stop="closePicker"
  >
    <template #btn="{ attrs, toggle }">
      <BaseInputText
        v-model="time"
        :aria-label="label ? undefined : ariaLabel"
        :borderless="borderless"
        :class="{
          'c-base-time-picker__trigger-input tw:w-auto tw:max-w-full':
            variant === 'dropdown',
        }"
        :disabled="disabled"
        :input-classes="variant === 'dropdown' ? 'tw:cursor-text' : ''"
        :label="label"
        new-design
        :wrapper-classes="
          variant === 'dropdown'
            ? 'tw:relative tw:min-w-0 tw:flex-1'
            : undefined
        "
        @click.stop="openPicker(toggle)"
        @focus="openPicker(toggle)"
      >
        <template
          v-if="$slots.prepend"
          #prepend
        >
          <slot name="prepend" />
        </template>
        <template
          v-if="variant === 'dropdown'"
          #append
        >
          <BaseButton
            :active="pickerOpen"
            active-color="secondary"
            :aria-controls="attrs['aria-controls']"
            :aria-expanded="attrs['aria-expanded']"
            :aria-haspopup="attrs['aria-haspopup']"
            :aria-label="$t('input_select.dropdown_button_label')"
            class="c-base-time-picker__chevron"
            :class="{ 'c-base-time-picker__chevron--open': pickerOpen }"
            color="form"
            data-testid="input-time-picker-chevron"
            :disabled="disabled"
            :icon="pickerOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
            icon-class="c-base-time-picker__chevron-icon"
            new-design
            tabindex="-1"
            @click.prevent.stop="togglePicker(toggle)"
            @mousedown.stop
          />
        </template>
      </BaseInputText>
    </template>
    <div class="c-base-time-picker__dropdown">
      <div class="c-base-time-picker__time-lists">
        <BaseInputTimePickerList
          v-model="hourOption"
          data-testid="hours-list"
          :disabled="disabled"
          :options="hourOptions"
        />
        <BaseInputTimePickerList
          v-model="minuteOption"
          data-testid="minutes-list"
          :disabled="disabled"
          :options="minuteOptions"
          :step="minuteStep"
        />
        <BaseInputTimePickerList
          v-if="showSeconds"
          v-model="secondOption"
          data-testid="seconds-list"
          :disabled="disabled"
          :options="secondOptions"
        />
      </div>
      <BaseSeparator />
      <div class="c-base-time-picker__footer">
        <BaseButton
          data-testid="input-time-picker-cancel"
          :disabled="disabled"
          :label="$t('global.cancel')"
          new-design
          size="sm"
          @click="cancel"
        />
        <BaseButton
          color="primary"
          data-testid="input-time-picker-ok"
          :disabled="disabled"
          :label="actionButtonLabel"
          new-design
          size="sm"
          @click="ok"
        />
      </div>
    </div>
  </BaseButtonDropdown>
</template>

<script>
import { ref, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import useTime from "shared/composables/useTime";

import BaseButton from "../BaseButton";
import BaseButtonDropdown from "../BaseButtonDropdown";
import BaseInputText from "../BaseInputText.vue";
import BaseInputTimePickerList from "../BaseInputTimePickerList";
import BaseSeparator from "../BaseSeparator";

export default {
  name: "BaseInputTimePicker",
  components: {
    BaseButton,
    BaseButtonDropdown,
    BaseSeparator,
    BaseInputTimePickerList,
    BaseInputText,
  },
  props: {
    modelValue: {
      type: Date,
      required: true,
    },
    minTime: {
      type: [Date, null],
      default: null,
    },
    maxTime: {
      type: [Date, null],
      default: null,
    },
    timezone: {
      type: String,
      default: "",
    },
    minuteStep: {
      type: Number,
      default: 1,
    },
    showSeconds: {
      type: Boolean,
    },
    interval: {
      type: Number,
      default: 0,
    },
    label: {
      type: String,
      default: "",
    },
    ariaLabel: {
      type: String,
      default: () => getLocaleText("global.time"),
    },
    borderless: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    actionButtonLabel: {
      type: String,
      default: () => getLocaleText("global.set"),
    },
    variant: {
      type: String,
      default: "default",
      validator: (value) => ["default", "dropdown"].includes(value),
    },
  },
  emits: ["update:modelValue"],
  setup(props, context) {
    const {
      error,
      hourOption,
      hourOptions,
      minuteOption,
      minuteOptions,
      pendingLocalTime,
      previousLocalTime,
      secondOption,
      secondOptions,

      localTime,
      setPreviousLocalTime,
      time,
    } = useTime(props, context);

    const pickerOpen = ref(false);

    function openPicker(toggle) {
      if (props.disabled || pickerOpen.value) return;

      toggle();
    }

    function togglePicker(toggle) {
      if (props.disabled) return;

      if (pickerOpen.value) {
        pickerOpen.value = false;

        return;
      }

      openPicker(toggle);
    }

    function closePicker() {
      pickerOpen.value = false;
    }

    function cancel() {
      context.emit("update:modelValue", previousLocalTime.value);
      pickerOpen.value = false;
    }

    function ok() {
      if (!error.value) {
        previousLocalTime.value = localTime.value;
        pendingLocalTime.value = null;
      }

      pickerOpen.value = false;
    }

    watch(pickerOpen, (value) => {
      if (value) {
        // When picker opens, save the previous local time if the user cancels
        setPreviousLocalTime();
      } else {
        // When picker closes, clear pending changes
        pendingLocalTime.value = null;
      }
    });

    return {
      hourOptions,
      hourOption,
      minuteOptions,
      minuteOption,
      secondOptions,
      secondOption,

      // eslint-disable-next-line vue/no-unused-properties -- These properties are used in tests
      pendingLocalTime,
      // eslint-disable-next-line vue/no-unused-properties -- These properties are used in tests
      previousLocalTime,
      // eslint-disable-next-line vue/no-unused-properties -- These properties are used in tests
      error,
      // eslint-disable-next-line vue/no-unused-properties -- These properties are used in tests
      localTime,
      time,

      pickerOpen,
      openPicker,
      togglePicker,
      closePicker,
      cancel,
      ok,
    };
  },
};
</script>

<style lang="scss" scoped>
.c-base-time-picker {
  &--dropdown {
    :deep(.input-container),
    :deep(.input-wrapper) {
      width: fit-content;
      max-width: 100%;
    }

    :deep(.input-wrapper) {
      --c-input-border-color: var(--s-color-denim-7);

      &:hover {
        --c-input-border-color: var(--s-color-denim-8);
      }

      &:focus-within {
        --c-input-border-color: var(--s-color-denim-10);
      }
    }

    :deep(.c-base-time-picker-list-container) {
      flex: 1 1 0;

      &::before,
      &::after {
        width: 100%;
      }
    }

    :deep(.c-base-time-picker-list),
    :deep(.c-base-time-picker__footer .base-button) {
      width: 100%;
    }
  }

  &--dropdown#{&}--open {
    :deep(.input-wrapper) {
      --c-input-border-color: var(--s-color-denim-10);
    }
  }

  &__chevron {
    --c-button-color: var(--s-color-denim-8);
    --c-button-color-hover: var(--s-color-denim-9);
  }

  &--dropdown#{&}--open &__chevron {
    --c-button-color: var(--s-color-denim-9);
  }

  :deep(.c-base-time-picker__chevron-icon) {
    transition: transform 100ms ease;
  }

  &__dropdown {
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: var(--s-spacing-sm);
  }

  &__time-lists {
    display: flex;
    overflow: hidden;
    height: calc(var(--s-size-controls-md) * 5);
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: var(--s-spacing-sm);
    padding: var(--s-spacing-sm);
  }
}
</style>
